import * as projectsService from '@/services/pm/projects'
import * as tasksService from '@/services/pm/tasks'

// import router from '@/router'

const DEFAULT_QUERY = {
  pagination: {
    total: 25,
    current: 1,
    pageSize: 25,
    pageSizeOptions: ['10', '25', '35', '45', '100'],
    showQuickJumper: true,
    showSizeChanger: true,
  },
  filters: null,
  sorter: { field: 'created_at', order: 'descend' },
}

const DEFAULT_META = {
  total: 25,
}

const DEFAULT_DATA = {
  data: null,
  loading: false,
  query: Object.assign({}, DEFAULT_QUERY),
  meta: Object.assign({}, DEFAULT_META),
}

const DEFAULT_COMP = {
  visible: false,
  loading: false,
  processing: false,
}

/*********************
 ** STORE OBJECT
 *********************/

export default {
  namespaced: true,
  state: {
    // DATA
    project: Object.assign({}, DEFAULT_DATA),
    project_tasks: Object.assign({}, DEFAULT_DATA),
    projects: Object.assign({}, DEFAULT_DATA),
    histories: Object.assign({}, DEFAULT_DATA),
    tasks: Object.assign({}, DEFAULT_DATA),
    contracts: Object.assign({}, DEFAULT_DATA),
    // NEW DATA
    new_project: { customer: null },
    new_contract: { customer: null, project: null },
    // UI
    ui: {
      CREATE_PROJECT: Object.assign({}, DEFAULT_COMP),
      BRIEFING: Object.assign({}, DEFAULT_COMP),
      CREATE_TASK: Object.assign({}, DEFAULT_COMP),
      CREATE_CONTRACT: Object.assign({}, DEFAULT_COMP),
    },
  },

  getters: {
    // Main Data
    data: (state) => (key) => state[key].data,
    loading: (state) => (key) => state[key].loading,
    query: (state) => (key) => state[key].query,
    pagination: (state) => (key) => state[key].query.pagination,
    filters: (state) => (key) => state[key].query.filters,
    sorter: (state) => (key) => state[key].query.sorter,
    meta: (state) => (key) => state[key].meta,
    // New Data
    new_project: (state) => state.new_project,
    // Components Data
    UI_VISIBLE: (state) => (key) => state.ui[key].visible,
    UI_LOADING: (state) => (key) => state.ui[key].loading,
    UI_PROCESSING: (state) => (key) => state.ui[key].processing,
  },

  mutations: {
    INIT: (state) => {
      state.project = state.projects = state.tasks = Object.assign({}, DEFAULT_DATA)
      for (const [key, data] of Object.entries(state.ui)) {
        state.ui[key] = Object.assign({}, DEFAULT_COMP)
      }
    },
    UPDATE: (state, { key, data }) => {
      if (state[key]) state[key].data = data
    },
    SET_LOADING: (state, { key, loading }) => {
      if (state[key]) state[key].loading = loading
    },
    START: (state, key) => {
      if (state[key]) state[key].loading = true
    },
    END: (state, key) => {
      if (state[key]) state[key].loading = false
    },
    SET_QUERY: (state, { key, query }) => {
      if (state[key]) state[key].query = query || Object.assign({}, DEFAULT_QUERY)
    },
    SET_PAGINATION: (state, { key, pagination }) => {
      if (state[key]) {
        state[key].query = state[key].query || Object.assign({}, DEFAULT_QUERY)
        state[key].query.pagination = pagination
      }
    },
    SET_FILTERS: (state, { key, filters }) => {
      if (state[key]) {
        state[key].query = state[key].query || Object.assign({}, DEFAULT_QUERY)
        state[key].query.filters = filters
      }
    },
    SET_SORTER: (state, { key, sorter }) => {
      if (state[key]) {
        state[key].query = state[key].query || Object.assign({}, DEFAULT_QUERY)
        state[key].query.sorter = sorter
      }
    },
    SET_META: (state, { key, meta }) => {
      if (meta) {
        if (meta.total) state[key].query.pagination.total = meta.total
        if (state[key]) state[key].meta = meta
      }
    },
    UI_VISIBLE: (state, { key, visible }) => {
      if (state.ui[key]) state.ui[key].visible = visible
    },
    UI_LOADING: (state, { key, loading }) => {
      if (state.ui[key]) state.ui[key].loading = loading
    },
    UI_PROCESSING: (state, { key, processing }) => {
      if (state.ui[key]) state.ui[key].processing = processing
    },
    // NEW DATA
    SET_NEW_PROJECT: (state, project) => {
      state.new_project = project
    },
    SET_NEW_PROJECT_CUSTOMER: (state, customer) => {
      state.new_project.customer = customer
    },

    SET_NEW_CONTRACT_META: (state, { customer, project }) => {
      state.new_contract.customer = customer
      state.new_contract.project = project
    },
  },
  actions: {
    // initialization
    init({ commit }) {
      commit('INIT')
    },
    // set pagination
    setPagination({ commit }, { key, pagination }) {
      if (pagination && pagination.current) commit('SET_PAGINATION', { key, pagination })
    },
    // set filters
    setFilters({ commit }, { key, filters }) {
      if (filters && Object.keys(filters).length) commit('SET_FILTERS', { key, filters })
    },
    // set sorter
    setSorter({ commit }, { key, sorter }) {
      if (sorter && sorter.field) commit('SET_SORTER', { key, sorter })
    },
    // set all query same time
    async updateQuery({ dispatch }, { key, query: { pagination, filters, sorter } }) {
      dispatch('setPagination', { key, pagination })
      dispatch('setFilters', { key, filters })
      dispatch('setSorter', { key, sorter })
      return await dispatch('fetchAll', key)
    },

    /*********************
     ** PROJECTS
     *********************/

    async fetchAll({ dispatch }, key) {
      if (key === 'projects') {
        return await dispatch('fetchProjects')
      } else if (key === 'tasks') {
        return await dispatch('fetchTasksByUser')
      } else if (key === 'project_tasks') {
        return await dispatch('fetchTasksByProject')
      } else {
        return null
      }
    },

    setData({ commit }, { key, data }) {
      commit('UPDATE', { key, data })
    },

    async fetchProjects({ commit, state }) {
      commit('START', 'projects')
      const { projects: data, meta } = await projectsService.fetchProjectsByQuery(state.projects.query)
      if (data) commit('UPDATE', { key: 'projects', data })
      if (meta) commit('SET_META', { key: 'projects', meta })
      commit('END', 'projects')
      return data
    },

    async fetchProject({ commit }, id) {
      commit('START', 'project')
      if (id) {
        const data = await projectsService.fetchProjectById(id)
        if (data) commit('UPDATE', { key: 'project', data })
        commit('END', 'project')
        return data
      }
      commit('END', 'project')
      return null
    },

    async createProject({ commit }, project) {
      commit('START', 'project')
      if (project) {
        const data = await projectsService.createProject(project)
        if (data) commit('UPDATE', { key: 'project', data })
        commit('END', 'project')
        return data
      }
      commit('END', 'project')
      return null
    },

    // contracts
    async fetchAllContracts({ commit, state }) {
      commit('START', 'contracts')
      const { contracts: data, meta } = await projectsService.fetchContractsByQuery(state.contracts.query)
      if (data) commit('UPDATE', { key: 'contracts', data })
      if (meta) commit('SET_META', { key: 'contracts', meta })
      commit('END', 'contracts')
      return data
    },

    async fetchContracts({ state, commit }) {
      commit('START', 'contracts')
      if (state.project && state.project.data._id) {
        const data = await projectsService.fetchContracts(state.project.data._id)
        if (data) commit('UPDATE', { key: 'contracts', data })
        commit('END', 'contracts')
        return data
      }
      return []
    },

    async insertContract({ state, commit }, contract) {
      commit('START', 'contracts')
      if (contract && state.project.data._id) {
        const data = await projectsService.insertContract(state.project.data._id, contract)
        if (data) commit('UPDATE', { key: 'contracts', data })
        commit('END', 'contracts')
        return data
      }
      commit('END', 'contracts')
      return []
    },

    // history
    async fetchHistory({ state, commit }) {
      commit('START', 'histories')
      if (state.project && state.project.data._id) {
        const data = await projectsService.fetchHistory(state.project.data._id)
        if (data) commit('UPDATE', { key: 'histories', data })
        commit('END', 'histories')
        return data
      }
      return []
    },

    async insertHistory({ state, commit }, content) {
      commit('START', 'histories')
      if (content && state.project.data._id) {
        const data = await projectsService.insertHistory(state.project.data._id, content)
        if (data) commit('UPDATE', { key: 'histories', data })
        commit('END', 'histories')
        return data
      }
      commit('END', 'histories')
      return []
    },

    // update
    async updateProject({ commit }, project) {
      commit('START', 'project')
      if (project) {
        const data = await projectsService.updateProject(project)
        if (data) commit('UPDATE', { key: 'project', data })
        commit('END', 'project')
        return data
      }
      commit('END', 'project')
      return null
    },

    // NEW DATA - Project
    setNewProject({ commit }, project) {
      commit('SET_NEW_PROJECT', project)
    },
    setNewProjectCustomer({ commit }, customer) {
      if (customer) {
        if (typeof customer === 'object' && customer._id) {
          commit('SET_NEW_PROJECT_CUSTOMER', customer)
        }
        if (typeof customer === 'string') {
          commit('SET_NEW_PROJECT_CUSTOMER', {
            _id: customer,
          })
        }
      } else {
        commit('SET_NEW_PROJECT_CUSTOMER', null)
      }
    },

    setNewContractMeta({ commit }, { customer, project }) {
      if ((customer, project)) {
        commit('SET_NEW_CONTRACT_META', { customer, project })
      }
    },

    /*********************
     **  TASKS
     *********************/

    async fetchTasksByProject({ state, commit }) {
      const project_id = state.project.data._id || null
      if (project_id) {
        commit('START', 'project_tasks')
        const data = await tasksService.fetchTasksByProjectId(project_id)
        if (data) commit('UPDATE', { key: 'project_tasks', data })
        commit('END', 'project_tasks')
        return data
      }
    },

    async fetchTasksByUser({ commit }) {
      commit('START', 'tasks')
      const data = await tasksService.fetchTasksByUser()
      if (data) commit('UPDATE', { key: 'tasks', data })
      commit('END', 'tasks')
      return data
    },

    async createTask({ commit }, task) {
      if (task) {
        commit('UI_PROCESSING', { key: 'CREATE_TASK', processing: true })
        const data = await tasksService.createTask(task)
        if (data) {
          // close task drawer
          commit('UI_VISIBLE', { key: 'CREATE_TASK', visible: false })
        }
        commit('UI_PROCESSING', { key: 'CREATE_TASK', processing: false })
        return data
      }
    },

    async updateTask({ commit }, task) {
      if (task) {
        const data = await tasksService.updateTask(task)
        return data
      }
    },
    // Components Handling
    /*********************
     **  COMPONENTS
     *********************/
    UI_VISIBLE({ commit }, { key, visible }) {
      commit('UI_VISIBLE', { key, visible })
    },
    open({ commit }, key) {
      commit('UI_VISIBLE', { key, visible: true })
    },
    close({ commit }, key) {
      commit('UI_VISIBLE', { key, visible: false })
    },
  },
}
